/*
  Lilibloom — pack picker (snippets/lb-pack-picker.liquid)

  Impact re-renders liquid blocks and the buy buttons on every variant change, so the picker element
  is replaced often. State therefore lives in window.LBPK (per product) and every new element
  re-hydrates from it. The edit dialog lives on <body>, outside the re-rendered area.

  Adding to cart: when more than one pair is selected, the product form submit is intercepted
  (capture phase) and all pairs are posted to /cart/add.js in one request, then the same events as
  Impact's ProductForm are dispatched so the cart drawer opens and refreshes.
*/
(() => {
  if (window.customElements.get('lb-pack-picker')) return;

  const store = (window.LBPK = window.LBPK || {});
  const state = (pid) => (store[pid] = store[pid] || { n: 1, pairs: [], sizeChosen: /[?&]variant=/.test(location.search) });
  const fmt = (cents) => '₪' + (Math.round(cents) / 100).toLocaleString('he-IL', { maximumFractionDigits: 2 });
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const PEN = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16z"/></svg>';

  class LbPackPicker extends HTMLElement {
    connectedCallback() {
      if (this.ready) return this.render();
      this.ready = true;
      this.data = JSON.parse(this.querySelector('script[data-lbpk]').textContent);
      this.formId = this.getAttribute('form-id');
      this.pid = this.getAttribute('product-id');
      this.st = state(this.pid);
      this.host = this.closest('product-rerender') || this.closest('.product-info') || document.body;
      this.sizeIdx = this.data.options.findIndex((o) => o.kind === 'size');
      this.colorIdx = this.data.options.findIndex((o) => o.kind === 'color');
      // edit order in the dialog: size, then color, then the rest (absorbency)
      this.editOrder = this.data.options
        .map((o, i) => i)
        .filter((i) => this.data.options[i].values.length > 1)
        .sort((a, b) => rank(this.data.options[a]) - rank(this.data.options[b]));

      const hint = this.editOrder.map((i) => this.data.options[i].name.replace(/^רמת /, '')).join(', ').replace(/, ([^,]*)$/, ' או $1');
      const hintEl = this.querySelector('.lbpk__bh span');
      if (hintEl) hintEl.textContent = hint ? `לחצי על זוג כדי לשנות ${hint}` : '';

      this.addEventListener('click', (e) => {
        const o = e.target.closest('.lbpk__o');
        if (o) return this.setN(+o.dataset.n);
        const t = e.target.closest('.lbpk__t');
        if (t) Dialog.open(this, +t.dataset.i, t);
      });
      this.addEventListener('keydown', (e) => {
        const o = e.target.closest('.lbpk__o');
        if (!o || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        const all = [...this.querySelectorAll('.lbpk__o')];
        const step = e.key === 'ArrowLeft' ? 1 : -1; // RTL: left moves forward
        const next = all[(all.indexOf(o) + step + all.length) % all.length];
        next.focus();
        this.setN(+next.dataset.n);
        e.preventDefault();
      });
      this.render();
    }

    get form() {
      return document.forms[this.formId];
    }

    pageVariant() {
      const id = +(this.form?.id?.value || 0);
      return this.data.variants.find((v) => v.id === id) || this.data.variants.find((v) => v.avail) || this.data.variants[0];
    }

    findVariant(opts) {
      return this.data.variants.find((v) => v.o.every((x, i) => x === opts[i]));
    }

    basePair() {
      const opts = [...this.pageVariant().o];
      // Impact always pre-selects a size; only trust it if the shopper picked it (or picked one in the dialog)
      if (this.sizeIdx > -1 && !this.st.sizeChosen) opts[this.sizeIdx] = this.st.size || null;
      return opts;
    }

    pct(n) {
      // sale mode (snippets/lb-sale-mode.liquid): sale tiers while <html> has lb-sale
      const tiers = (document.documentElement.classList.contains('lb-sale') && this.data.saleTiers) || this.data.tiers;
      return tiers.reduce((p, [q, d]) => (n >= q ? d : p), 0);
    }

    setN(n) {
      const st = this.st;
      // coming from a single pair: start the pack from what is selected on the page now
      if (st.n === 1 && n > 1) st.pairs = [];
      st.n = n;
      if (n > 1) {
        if (!st.pairs.length) st.pairs.push(this.basePair());
        while (st.pairs.length < n) st.pairs.push([...st.pairs[0]]);
        st.pairs.length = n;
      }
      this.querySelector('.lbpk__err').hidden = true;
      this.render();
    }

    render() {
      const st = this.st;
      const multi = st.n > 1;
      const price = this.pageVariant().price;

      this.querySelectorAll('.lbpk__o').forEach((o) => {
        const k = +o.dataset.n;
        const on = k === st.n;
        o.classList.toggle('is-on', on);
        o.setAttribute('aria-checked', on);
        o.tabIndex = on ? 0 : -1;
        o.querySelector('.lbpk__pp').textContent = k === 1 ? fmt(price) : `${fmt(Math.floor(price * (1 - this.pct(k) / 100) / 100) * 100)} לזוג`;
      });

      this.host.classList.toggle('lbpk-multi', multi);
      this.querySelector('.lbpk__bundle').hidden = !multi;
      this.updateButtons();
      if (!multi) return this.topPrice(null);

      // drop size from pairs if the picker was re-rendered for a product whose sizes differ (safety)
      st.pairs = st.pairs.map((p) => p.map((v, i) => (v === null || this.data.options[i].values.some((x) => x.v === v) ? v : null)));

      const tiles = this.querySelector('.lbpk__tiles');
      tiles.style.setProperty('--n', st.n);
      tiles.dataset.n = st.n;
      tiles.innerHTML = st.pairs.map((p, i) => this.tileHtml(p, i)).join('');
      this.querySelector('.lbpk__count').textContent = `${st.n} זוגות`;

      const full = st.pairs.reduce((a, p) => a + this.pairPrice(p), 0);
      const off = Math.round((full * this.pct(st.n)) / 100);
      this.topPrice({ full, off });
    }

    // pack mode: the product price at the top shows the pack total, like the pack boxes promise
    topPrice(total) {
      const block = this.host.querySelector('.product-info__price');
      if (!block) return;
      let el = block.querySelector('.lbpk-price');
      if (!total) return el?.remove();
      if (!el) {
        el = document.createElement('div');
        el.className = 'lbpk-price';
        el.setAttribute('aria-live', 'polite');
        el.innerHTML = '<price-list class="price-list price-list--lg"><sale-price class="text-lg"><span class="sr-only">מחיר המארז</span><span data-p></span></sale-price><compare-at-price class="text-subdued line-through"><span class="sr-only">מחיר רגיל</span><span data-c></span></compare-at-price></price-list><span class="lbpk-price__save"></span>';
        (block.querySelector('.rating-with-text') || block).prepend(el);
      }
      el.querySelector('[data-p]').textContent = fmt(total.full - total.off);
      el.querySelector('[data-c]').textContent = fmt(total.full);
      el.querySelector('.lbpk-price__save').textContent = `חוסכת ${fmt(total.off)}`;
    }

    pairPrice(p) {
      const v = this.findVariant(p) || this.data.variants.find((x) => x.o.every((val, i) => p[i] === null || val === p[i])) || this.pageVariant();
      return v.price;
    }

    imageFor(p) {
      const vs = this.data.variants;
      const c = this.colorIdx;
      const v = this.findVariant(p) || (c > -1 && vs.find((x) => x.o[c] === p[c] && x.img)) || vs.find((x) => x.img);
      return v?.img || '';
    }

    tileHtml(p, i) {
      const opts = this.data.options;
      const missing = this.sizeIdx > -1 && p[this.sizeIdx] === null;
      const v = missing ? null : this.findVariant(p);
      const soldOut = !missing && (!v || !v.avail);
      const head = this.colorIdx > -1 ? p[this.colorIdx] : p.find((x, k) => k !== this.sizeIdx) || '';
      const rest = opts
        .map((o, k) => k)
        .filter((k) => k !== this.colorIdx && opts[k].values.length > 1 || k === this.sizeIdx)
        .sort((a, b) => (a === this.sizeIdx ? -1 : b === this.sizeIdx ? 1 : 0))
        .map((k) => (k === this.sizeIdx && missing ? '<u>בחרי מידה</u>' : esc(p[k])))
        .join(' · ');
      const label = `זוג ${i + 1}: ${[head, ...p.filter((x, k) => k !== this.colorIdx && x)].join(', ')}${missing ? ', לא נבחרה מידה' : ''}${soldOut ? ', אזל במלאי' : ''}. לחצי לשינוי`;
      const img = this.imageFor(p);
      return `<button type="button" class="lbpk__t${missing ? ' is-missing' : ''}${soldOut ? ' is-sold' : ''}" data-i="${i}" aria-label="${esc(label)}">${img ? `<img src="${esc(img)}" alt="" loading="lazy" width="400" height="533">` : ''}<span class="lbpk__pen">${PEN}</span>${soldOut ? '<span class="lbpk__flag">אזל</span>' : ''}<span class="lbpk__tc"><b>${esc(head)}</b>${rest}</span></button>`;
    }

    submitButtons() {
      const f = this.form;
      if (!f) return [];
      return [...document.querySelectorAll(`button[type="submit"][form="${this.formId}"]`), ...f.querySelectorAll('button[type="submit"]')];
    }

    updateButtons() {
      const multi = this.st.n > 1;
      this.submitButtons().forEach((btn) => {
        const box = btn.querySelector(':scope > div') || btn;
        if (!btn.hasAttribute('data-lbpk-orig')) {
          btn.setAttribute('data-lbpk-orig', box.textContent.trim());
          btn.setAttribute('data-lbpk-disabled', btn.disabled ? '1' : '');
        }
        box.textContent = multi ? `הוספת ${this.st.n} זוגות לסל` : btn.getAttribute('data-lbpk-orig');
        // the page variant may be sold out; in pack mode each pair is checked on its own
        btn.disabled = multi ? false : btn.getAttribute('data-lbpk-disabled') === '1';
        btn.classList.toggle('button--subdued', !multi && btn.disabled);
      });
    }

    showError(msg) {
      const err = this.querySelector('.lbpk__err');
      err.textContent = msg;
      err.hidden = !msg;
    }

    async addToCart() {
      const st = this.st;
      const opts = this.data.options;

      const missingAt = st.pairs.findIndex((p) => this.sizeIdx > -1 && p[this.sizeIdx] === null);
      if (missingAt > -1) {
        this.showError('נשאר לבחור מידה. לחצי על הזוג כדי לבחור.');
        return Dialog.open(this, missingAt, this.querySelectorAll('.lbpk__t')[missingAt], 'בחרי מידה לזוג הזה');
      }
      const counts = new Map();
      for (let i = 0; i < st.pairs.length; i++) {
        const v = this.findVariant(st.pairs[i]);
        if (!v || !v.avail) {
          this.showError(`זוג ${i + 1} אזל במלאי. לחצי עליו כדי לבחור צבע או מידה אחרים.`);
          return Dialog.open(this, i, this.querySelectorAll('.lbpk__t')[i], 'השילוב הזה אזל. בחרי צבע או מידה אחרים');
        }
        counts.set(v, (counts.get(v) || 0) + 1);
      }
      for (const [v, q] of counts) {
        if (v.inv !== null && q > v.inv) {
          const name = v.o.filter((x, k) => opts[k].values.length > 1).join(' · ');
          this.showError(`נשארו במלאי רק ${v.inv} זוגות של ${name}. החליפי צבע או מידה באחד הזוגות.`);
          return;
        }
      }
      this.showError('');

      const form = this.form;
      const buttons = this.submitButtons();
      buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      try {
        /* Hidden line-item properties (a leading underscore keeps them out of the cart, checkout and emails;
           they stay on the order in the admin and the API). They tell us which orders came from the pack picker:
             _pack_source  always "picker"
             _pack_size    how many pairs the shopper chose (3 or 5)
             _pack_id      one id per click, so the lines of one pack can be grouped */
        const packId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
        const properties = { _pack_source: 'picker', _pack_size: String(st.pairs.length), _pack_id: packId };
        const items = [...counts].map(([v, quantity]) => ({ id: v.id, quantity, properties }));
        const sections = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: sections.join(','), sections_url: `${root}variants/${items[0].id}` }),
        });
        const json = await res.json();
        if (!res.ok) {
          this.showError(json.description || json.message || 'לא הצלחנו להוסיף לסל. נסי שוב.');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }
        const tv = window.themeVariables?.settings || {};
        if (tv.cartType === 'page' || tv.pageType === 'cart') return (window.location.href = `${root}cart`);
        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = json.sections;
        form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      } catch (e) {
        this.showError('לא הצלחנו להוסיף לסל. נסי שוב.');
      } finally {
        buttons.forEach((b) => b.removeAttribute('aria-busy'));
      }
    }
  }

  function rank(o) {
    return o.kind === 'size' ? 0 : o.kind === 'color' ? 1 : 2;
  }

  /* Sizebay lives inside the page's size picker (hidden in pack mode). The dialog reuses its buttons
     and shows its recommendation ("✓ ההמלצה שלנו: M") once the shopper has a profile. */
  const sizebay = {
    button(host, name) {
      return host.querySelector(name === 'vfr' ? '#szb-vfr-button' : '#szb-measurements-button');
    },
    recommendation(host, sizes) {
      const text = host.querySelector('#szb-vfr-recommendation')?.textContent || '';
      const size = text.split(':').pop().trim();
      return text && sizes.includes(size) ? size : null;
    },
  };

  /* ---------- edit dialog (one per page, on <body>) ---------- */
  const Dialog = {
    el: null,
    build() {
      if (this.el) return this.el;
      const el = document.createElement('div');
      el.className = 'lbpk-modal';
      el.hidden = true;
      el.dir = 'rtl';
      el.innerHTML = `<div class="lbpk-modal__box" role="dialog" aria-modal="true" aria-labelledby="lbpk-mt">
          <button type="button" class="lbpk-modal__x" aria-label="סגירה">×</button>
          <div class="lbpk-modal__head"><img alt=""><div><b id="lbpk-mt"></b><span></span></div></div>
          <p class="lbpk-modal__msg" hidden></p>
          <div class="lbpk-modal__groups"></div>
          <button type="button" class="lbpk-modal__ok">עדכון</button>
        </div>`;
      document.body.appendChild(el);
      el.addEventListener('click', (e) => {
        if (e.target === el || e.target.closest('.lbpk-modal__x')) return this.close();
        const szb = e.target.closest('[data-szb]');
        if (szb) return sizebay.button(this.picker.host, szb.dataset.szb)?.click();
        const rec = e.target.closest('[data-rec]');
        if (rec) {
          this.draft[this.picker.sizeIdx] = rec.dataset.rec;
          return this.fill();
        }
        const c = e.target.closest('[data-v]');
        if (c) {
          this.draft[+c.parentElement.dataset.k] = c.dataset.v;
          return this.fill();
        }
        if (e.target.closest('.lbpk-modal__ok')) this.commit();
      });
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') return this.close();
        if (e.key !== 'Tab') return;
        const f = [...el.querySelectorAll('button:not([disabled])')];
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
        else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
      });
      return (this.el = el);
    },
    open(picker, i, returnTo, msg) {
      this.build();
      this.picker = picker;
      this.i = i;
      this.returnTo = returnTo;
      this.draft = [...picker.st.pairs[i]];
      const m = this.el.querySelector('.lbpk-modal__msg');
      m.textContent = msg || '';
      m.hidden = !msg;
      this.fill();
      this.el.hidden = false;
      this.szbObserver?.disconnect();
      const szbRoot = picker.host.querySelector('[id^="szb-look-"]');
      if (szbRoot) {
        this.szbObserver = new MutationObserver(() => !this.el.hidden && this.fill());
        this.szbObserver.observe(szbRoot, { subtree: true, childList: true, characterData: true });
      }
      document.documentElement.classList.add('lbpk-lock');
      (this.el.querySelector('.lbpk-modal__groups .is-on') || this.el.querySelector('.lbpk-modal__groups button'))?.focus();
    },
    close() {
      this.el.hidden = true;
      this.szbObserver?.disconnect();
      document.documentElement.classList.remove('lbpk-lock');
      // the tile may have been re-rendered meanwhile; focus its current equivalent
      const tile = this.picker?.querySelectorAll?.('.lbpk__t')[this.i];
      (tile && tile.isConnected ? tile : this.returnTo)?.focus?.();
    },
    available(k, val) {
      const p = this.picker;
      const want = this.draft.map((x, j) => (j === k ? val : x));
      return p.data.variants.some((v) => v.avail && v.o.every((x, j) => want[j] === null || x === want[j]));
    },
    fill() {
      const p = this.picker;
      const d = this.draft;
      const opts = p.data.options;
      this.el.querySelector('#lbpk-mt').textContent = `זוג ${this.i + 1}`;
      this.el.querySelector('.lbpk-modal__head span').textContent = d.filter(Boolean).join(' · ');
      const img = p.imageFor(d);
      const im = this.el.querySelector('.lbpk-modal__head img');
      im.hidden = !img;
      if (img) im.src = img;

      this.el.querySelector('.lbpk-modal__groups').innerHTML = p.editOrder
        .map((k) => {
          const o = opts[k];
          const cur = d[k];
          const isColor = o.kind === 'color';
          const isCards = !isColor && o.values.some((x) => x.d);
          const isSize = k === p.sizeIdx;
          const rec = isSize ? sizebay.recommendation(p.host, o.values.map((x) => x.v)) : null;
          const btns = o.values
            .map(({ v, bg, d: desc }) => {
              const on = v === cur;
              const dis = !this.available(k, v);
              const cls = `${on ? 'is-on' : ''}${dis ? ' is-disabled' : ''}${v === rec ? ' is-rec' : ''}`;
              const label = `${v}${dis ? ' (אזל)' : ''}`;
              return isColor
                ? `<button type="button" role="radio" aria-checked="${on}" data-v="${esc(v)}" title="${esc(label)}" aria-label="${esc(label)}" class="${cls}" style="--sw:${esc(bg || v)}"></button>`
                : isCards
                ? `<button type="button" role="radio" aria-checked="${on}" data-v="${esc(v)}" aria-label="${esc(label)}${desc ? `, ${esc(desc)}` : ''}" class="${cls}"><b>${esc(v)}</b>${desc ? `<small>${esc(desc)}</small>` : ''}</button>`
                : `<button type="button" role="radio" aria-checked="${on}" data-v="${esc(v)}" aria-label="${esc(label)}" class="${cls}">${esc(v)}</button>`;
            })
            .join('');
          const shown = cur === null ? '<em>בחרי מידה</em>' : esc(cur);
          let szb = '';
          if (isSize && sizebay.button(p.host, 'vfr')) {
            szb = `<span class="lbpk-szb">${rec ? '' : '<i>מתלבטת?</i>'}<button type="button" data-szb="vfr">שאלון התאמה</button>${sizebay.button(p.host, 'measurements') ? '<button type="button" data-szb="measurements">טבלת מידות</button>' : ''}</span>`;
          }
          const recLine = rec ? `<button type="button" class="lbpk-szb__rec${cur === rec ? ' is-on' : ''}" data-rec="${esc(rec)}">✓ ההמלצה שלנו: ${esc(rec)}${cur === rec ? '' : ' · לבחירה'}</button>` : '';
          return `<div class="lbpk-modal__grp"><div class="lbpk-modal__l"><span class="lbpk-modal__lt">${esc(o.name)}: <span>${shown}</span></span>${szb}</div>${recLine}<div class="${isColor ? 'lbpk-sw' : isCards ? 'lbpk-abs' : 'lbpk-chips'}" role="radiogroup" aria-label="${esc(o.name)}" data-k="${k}">${btns}</div></div>`;
        })
        .join('');

      const ok = this.el.querySelector('.lbpk-modal__ok');
      const missing = p.sizeIdx > -1 && d[p.sizeIdx] === null;
      const v = missing ? null : p.findVariant(d);
      ok.disabled = missing || !v || !v.avail;
      ok.textContent = missing ? 'בחרי מידה' : !v || !v.avail ? 'אזל במלאי' : 'עדכון';
    },
    commit() {
      const p = this.picker;
      const st = p.st;
      const s = p.sizeIdx;
      st.pairs[this.i] = this.draft;
      // a size picked here also fills every pair that still has no size
      if (s > -1 && this.draft[s] !== null) {
        if (!st.sizeChosen) st.size = this.draft[s];
        st.pairs.forEach((pair) => {
          if (pair[s] === null) pair[s] = this.draft[s];
        });
      }
      const live = document.querySelector(`lb-pack-picker[product-id="${p.pid}"]`) || p;
      this.picker = live;
      live.showError('');
      live.render();
      this.close();
    },
  };

  /* ---------- page-level listeners (registered once) ---------- */

  // remember that the shopper actively picked a size on the page
  document.addEventListener('change', (e) => {
    const input = e.target.closest?.('input[data-option-position]');
    if (!input) return;
    const picker = document.querySelector(`lb-pack-picker[form-id="${input.getAttribute('form')}"]`);
    if (picker && +input.dataset.optionPosition === picker.sizeIdx + 1) picker.st.sizeChosen = true;
  });

  // pack mode: take over the product form submit before Impact's ProductForm sees it
  document.addEventListener(
    'submit',
    (e) => {
      // form.id would return the form's <input name="id">, so read the attribute
      const formId = e.target?.getAttribute?.('id');
      const picker = formId && document.querySelector(`lb-pack-picker[form-id="${formId}"]`);
      if (!picker || picker.st.n < 2) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      picker.addToCart();
    },
    true
  );

  window.customElements.define('lb-pack-picker', LbPackPicker);
})();
